import React, { useEffect, useMemo, useState } from 'react'
import { Button } from 'antd'
import styled from 'styled-components'
import ReviewUploadsSidebar from './ReviewUploadsSidebar'
import ReviewUploadFocusCard from './ReviewUploadFocusCard'
import {
  buildUploadReviewSidebarItems,
  findUploadReviewDraft,
  findUploadReviewFile,
  getUploadReviewFooterCopy,
  isUploadReviewSessionComplete,
  resolveUploadReviewFocusId,
} from 'src/utils/invoiceUploadReviewHelpers'
import {
  dismissCnQueueTeach,
  getQuietUploadProgressLine,
  shouldShowCnQueueTeach,
  shouldUseCompactUploadReviewChrome,
} from 'src/utils/invoiceUploadQueuePolish'
import { getUploadQueueProgress } from 'src/utils/invoiceUploadQueueHelpers'
import {
  UPLOAD_LOOP_CLOSE_COPY,
  dismissWhyUploadTeach,
  shouldShowWhyUploadTeach,
} from 'src/utils/invoiceUploadLoopClose'

/**
 * Chunk U3/U6/L1 — Review uploads panel: sidebar (pick order) + one focus card.
 * L1: teach-once Why upload? for Free / single-site.
 */
function ReviewUploadsPanel({
  drafts = [],
  files = [],
  venueId,
  venueSuppliers = [],
  progressLine = '',
  ocrActive = false,
  focusId: controlledFocusId = null,
  onFocusIdChange,
  userPickedFocus = false,
  onUserPickFocus,
  confirmLoading = false,
  actionsDisabled = false,
  currentPlan = null,
  venueCount = 0,
  isAdmin = false,
  onConfirmAll,
  canConfirmAll = false,
  confirmAllLabel = '',
  onRemoveFocus,
  onRetryOcr,
  onReviewLater,
  onDiscardRemaining,
  onUpdateSupplier,
  onCreateSupplierFromUpload,
  onUpdateField,
  onUpdateTotal,
  onUseEstimate,
  onUpdatePurchaseOrder,
  previewZoomByDraftId = {},
  onPreviewZoomChange,
}) {
  const copy = getUploadReviewFooterCopy()
  const [localFocusId, setLocalFocusId] = useState(controlledFocusId)
  const [cnTeachTick, setCnTeachTick] = useState(0)
  const [whyUploadTick, setWhyUploadTick] = useState(0)

  const focusId = controlledFocusId != null ? controlledFocusId : localFocusId

  const setFocusId = nextId => {
    setLocalFocusId(nextId)
    if (typeof onFocusIdChange === 'function') onFocusIdChange(nextId)
  }

  const focusDraft = findUploadReviewDraft(drafts, focusId)
  const focusFile = findUploadReviewFile(files, focusId)
  const sessionDone = isUploadReviewSessionComplete(drafts)

  useEffect(() => {
    const next = resolveUploadReviewFocusId(drafts, focusId, {
      userPicked: userPickedFocus,
    })
    if (next !== focusId) {
      setFocusId(next)
    }
  }, [drafts, userPickedFocus, focusId])

  useEffect(() => {
    if (sessionDone || focusDraft) return
    const next = resolveUploadReviewFocusId(drafts, focusId, {
      userPicked: userPickedFocus,
    })
    if (next && next !== focusId) {
      setFocusId(next)
    }
  }, [drafts, focusDraft, sessionDone, focusId, userPickedFocus])

  const sidebarItems = useMemo(
    () => buildUploadReviewSidebarItems(drafts, files),
    [drafts, files],
  )

  const compact = shouldUseCompactUploadReviewChrome(sidebarItems.length)
  const progress = getUploadQueueProgress(drafts)
  const quietProgress = getQuietUploadProgressLine({
    progressLine,
    reading: progress.reading,
    queued: progress.queued,
    ocrActive,
  })

  const showCnTeach = shouldShowCnQueueTeach(venueId, {
    isCreditNote: focusFile?.documentType === 'creditNote',
  })
  // cnTeachTick / whyUploadTick force re-render after dismiss
  void cnTeachTick
  void whyUploadTick

  const showWhyUpload = shouldShowWhyUploadTeach(venueId, {
    currentPlan,
    venueCount,
    isAdmin,
  })

  const handleSelect = id => {
    if (typeof onUserPickFocus === 'function') onUserPickFocus(true)
    setFocusId(String(id))
  }

  return (
    <Panel>
      {quietProgress ? <ProgressLine>{quietProgress}</ProgressLine> : null}
      {showWhyUpload ? (
        <WhyUploadTeach>
          <WhyUploadTitle>
            {UPLOAD_LOOP_CLOSE_COPY.whyUploadTitle}
          </WhyUploadTitle>
          <WhyUploadBody>{UPLOAD_LOOP_CLOSE_COPY.whyUploadBody}</WhyUploadBody>
          <WhyUploadDismiss
            type='button'
            onClick={() => {
              dismissWhyUploadTeach(venueId)
              setWhyUploadTick(t => t + 1)
            }}
          >
            {UPLOAD_LOOP_CLOSE_COPY.whyUploadGotIt}
          </WhyUploadDismiss>
        </WhyUploadTeach>
      ) : null}
      {!compact ? (
        <Intro>
          Review each file in the list and fill in any missing details. When
          every upload is ready, use Confirm all below to save this batch.
        </Intro>
      ) : null}
      <Body data-compact={compact ? 'true' : undefined}>
        {!compact ? (
          <ReviewUploadsSidebar
            items={sidebarItems}
            focusId={focusId}
            onSelect={handleSelect}
          />
        ) : null}
        <FocusColumn>
          {sessionDone && !focusDraft ? (
            <DoneState>
              <h3>{copy.allDoneTitle}</h3>
              <p>{copy.allDoneBody}</p>
            </DoneState>
          ) : (
            <ReviewUploadFocusCard
              file={focusFile}
              draft={focusDraft}
              venueId={venueId}
              venueSuppliers={venueSuppliers}
              previewZoom={previewZoomByDraftId[String(focusId)] || 1}
              onPreviewZoomChange={value =>
                onPreviewZoomChange &&
                onPreviewZoomChange(String(focusId), value)
              }
              onUpdateSupplier={(id, title) =>
                onUpdateSupplier && onUpdateSupplier(focusId, id, title)
              }
              onCreateSupplierFromUpload={title =>
                onCreateSupplierFromUpload &&
                onCreateSupplierFromUpload(focusId, title)
              }
              onUpdateField={(field, value) =>
                onUpdateField && onUpdateField(focusId, field, value)
              }
              onUpdateTotal={value =>
                onUpdateTotal && onUpdateTotal(focusId, value)
              }
              onUseEstimate={() => onUseEstimate && onUseEstimate(focusId)}
              onUpdatePurchaseOrder={poUpdate =>
                onUpdatePurchaseOrder &&
                onUpdatePurchaseOrder(focusId, poUpdate)
              }
              onRemove={() => onRemoveFocus && onRemoveFocus(focusId)}
              onRetryOcr={() => onRetryOcr && onRetryOcr(focusId)}
              actionsDisabled={actionsDisabled}
              showCnTeach={showCnTeach}
              onDismissCnTeach={() => {
                dismissCnQueueTeach(venueId)
                setCnTeachTick(t => t + 1)
              }}
            />
          )}
        </FocusColumn>
      </Body>
      <Footer>
        <FooterSecondary>
          <Button onClick={onReviewLater} disabled={actionsDisabled}>
            {copy.reviewLater}
          </Button>
          <Button
            type='danger'
            ghost
            onClick={onDiscardRemaining}
            disabled={actionsDisabled}
          >
            {copy.discardRemaining}
          </Button>
        </FooterSecondary>
        <ConfirmAllWrap>
          {copy.confirmAllHint ? (
            <ConfirmAllHint>{copy.confirmAllHint}</ConfirmAllHint>
          ) : null}
          <Button
            type='primary'
            size='large'
            loading={confirmLoading}
            disabled={!canConfirmAll || actionsDisabled || confirmLoading}
            onClick={onConfirmAll}
            style={{ background: '#b5d922', borderColor: '#b5d922' }}
          >
            {confirmAllLabel || copy.confirmAll(0)}
          </Button>
        </ConfirmAllWrap>
      </Footer>
    </Panel>
  )
}

export default ReviewUploadsPanel

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
`

const ProgressLine = styled.p`
  margin: 0;
  font-size: 14px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.75);
`

const Intro = styled.p`
  margin: 0;
  font-size: 13px;
  color: rgba(0, 0, 0, 0.55);
  line-height: 1.45;
`

const WhyUploadTeach = styled.div`
  padding: 10px 12px;
  border-radius: 6px;
  background: #f6ffed;
  border: 1px solid #b7eb8f;
`

const WhyUploadTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #135200;
  margin-bottom: 4px;
`

const WhyUploadBody = styled.p`
  margin: 0 0 8px;
  font-size: 13px;
  color: #237804;
  line-height: 1.45;
`

const WhyUploadDismiss = styled.button`
  padding: 0;
  border: none;
  background: transparent;
  color: #389e0d;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  text-decoration: underline;

  &:hover {
    color: #237804;
  }
`

const Body = styled.div`
  display: flex;
  gap: 16px;
  align-items: stretch;
  min-height: 420px;

  &[data-compact='true'] {
    min-height: 0;
  }
`

const FocusColumn = styled.div`
  flex: 1;
  min-width: 0;
  max-height: min(70vh, 640px);
  overflow-y: auto;
  padding-right: 4px;
`

const DoneState = styled.div`
  padding: 48px 16px;
  text-align: center;

  h3 {
    margin: 0 0 8px;
  }

  p {
    margin: 0;
    color: rgba(0, 0, 0, 0.65);
  }
`

const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 12px;
  padding-top: 12px;
  border-top: 1px solid #f0f0f0;
`

const FooterSecondary = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`

const ConfirmAllWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
  margin-left: auto;
`

const ConfirmAllHint = styled.p`
  margin: 0;
  max-width: 280px;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.55);
  text-align: right;
  line-height: 1.4;
`
